import { useMemo } from 'react'
import { calculateUsdAmountFromToken, formatUsd } from '@components/Web3/useEthPrice'
import { ETH_ADDRESS } from 'use-towns-client'

import { formatUnitsToFixedLength } from './useBalance'

/**
 * Normalize tip amounts to 18 decimals (ETH format) for consistent comparison and storage.
 * - ETH amounts are already in 18 decimals
 * - USDC amounts are in 6 decimals and need to be multiplied by 10^12
 * @param amount - The tip amount in its native decimal format
 * @param currency - The currency address
 * @returns Amount normalized to 18 decimals
 */
export function normalizeToEthDecimals(amount: bigint, currency: string): bigint {
    const isUsdc = currency.toLowerCase() !== ETH_ADDRESS.toLowerCase()
    return isUsdc ? amount * 10n ** 12n : amount
}

/**
 * Format a tip amount in normalized 18-decimal format as USD.
 * Used for displaying tip amounts that combine both ETH and USDC.
 * @param amount18Decimals - Amount already normalized to 18 decimals
 * @returns Formatted USD string (e.g., "$3.23")
 */
export function format18DecimalAsUsd(
    amount18Decimals: bigint,
    opts?: { noDecimals?: boolean },
): string {
    return formatUsd(formatUnitsToFixedLength(amount18Decimals, 18, 2), {
        noDecimals: opts?.noDecimals,
    })
}

export function useFormattedNormalizedTipAmount(
    amount: bigint,
    currency: string,
    opts?: { noDecimals?: boolean },
): string {
    return useMemo(() => {
        const normalizedAmount = normalizeToEthDecimals(amount, currency)
        return format18DecimalAsUsd(normalizedAmount, { noDecimals: opts?.noDecimals })
    }, [amount, currency, opts?.noDecimals])
}

/**
 * Extract ETH and USDC amounts from a currency-indexed object.
 * @param currencyToAmounts - Object mapping currency addresses to amounts
 * @returns { ethAmount, usdcAmount } breakdown
 */
export function getCurrencyBreakdown(currencyToAmounts: Record<string, string | bigint>): {
    ethAmount: bigint
    usdcAmount: bigint
} {
    let ethAmount = 0n
    let usdcAmount = 0n

    Object.entries(currencyToAmounts).forEach(([currency, amount]) => {
        const amountBigInt = typeof amount === 'string' ? BigInt(amount || '0') : amount
        if (currency.toLowerCase() === ETH_ADDRESS.toLowerCase()) {
            ethAmount += amountBigInt
        } else {
            usdcAmount += amountBigInt
        }
    })

    return { ethAmount, usdcAmount }
}

/**
 * Format tips broken down by currency into USD, using current ETH price.
 * Handles ETH → USD conversion and USDC normalization separately.
 * @param ethAmount - Amount in ETH (18 decimals)
 * @param usdcAmount - Amount in USDC (6 decimals)
 * @param ethPrice - Current ETH price in USD
 * @returns Formatted USD string (e.g., "$3.23")
 */
export function useCombinedEthAndUSDCInUSD(
    ethAmount: bigint,
    usdcAmount: bigint,
    ethPrice?: string,
    opts?: { noDecimals?: boolean },
): string {
    return useMemo(() => {
        return combineEthAndUsdcInUsd({ ethAmount, usdcAmount, ethPrice, opts })
    }, [ethAmount, usdcAmount, ethPrice, opts])
}

/**
 * Calculate combined USD value from ETH and USDC amounts.
 * Returns the value as a bigint in 18 decimals for comparison/sorting.
 * @param ethAmount - Amount in ETH (18 decimals)
 * @param usdcAmount - Amount in USDC (6 decimals)
 * @param ethPrice - Current ETH price in USD
 * @returns Combined USD value as bigint (18 decimals), or 0n if ethPrice unavailable
 */
export function calculateCombinedUsdValue(args: {
    ethAmount: bigint
    usdcAmount: bigint
    ethPrice?: string
}): bigint {
    const { ethAmount, usdcAmount, ethPrice } = args
    if (!ethPrice) {
        return 0n
    }

    // Convert ETH to USD using current price
    let ethAmountInUsd = calculateUsdAmountFromToken({
        tokenAmount: ethAmount,
        tokenPriceInUsd: ethPrice,
    })
    if (typeof ethAmountInUsd !== 'bigint') {
        ethAmountInUsd = 0n
    }

    // Normalize USDC UP to 18 decimals (1 USDC ≈ $1)
    const usdcAmountIn18Decimals = normalizeToEthDecimals(usdcAmount, '0x1')

    return ethAmountInUsd + usdcAmountIn18Decimals
}

export function combineEthAndUsdcInUsd(args: {
    ethAmount: bigint
    usdcAmount: bigint
    ethPrice?: string
    opts?: { noDecimals?: boolean }
}): string {
    const { ethAmount, usdcAmount, ethPrice, opts } = args
    if (!ethPrice) {
        return '--'
    }

    const totalAmountInUsd = calculateCombinedUsdValue({ ethAmount, usdcAmount, ethPrice })
    const formatted = format18DecimalAsUsd(totalAmountInUsd, {
        noDecimals: opts?.noDecimals,
    })

    // Guard against invalid formatting
    if (isNaN(Number(formatted.replace('$', '').replaceAll(',', '')))) {
        return '--'
    }

    return formatted
}
